import { useQuery } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import {
  DashboardApi,
  type DashboardApiDashboardControllerGetCurrentTasksByPhaseRequest,
} from "@/config/apiClient/api";
import { RoleEnum } from "@/config/apiClient.types";
import { createApiClient } from "@/config/createApiClient";
import { selectedClientServiceIdAtom, userAtom } from "@/store";

const apiClient = createApiClient(DashboardApi);

export const useGetCurrentTasksByPhase = ({
  phase,
  limit = 10,
  offset = 0,
}: {
  phase?: DashboardApiDashboardControllerGetCurrentTasksByPhaseRequest["phase"];
  limit?: number;
  offset?: number;
}) => {
  const selectedClientServiceIdAtomValue = useAtomValue(
    selectedClientServiceIdAtom
  );

  return useQuery({
    queryKey: [
      "dashboard",
      "current-tasks",
      "by-phase",
      phase,
      limit,
      offset,
      selectedClientServiceIdAtomValue,
    ],
    enabled: selectedClientServiceIdAtomValue > 0,
    queryFn: () =>
      apiClient.dashboardControllerGetCurrentTasksByPhase({
        clientServiceId: selectedClientServiceIdAtomValue,
        phase,
        limit,
        offset,
      }),
  });
};

export const useGetDeliveredTasks = ({ limit = 10, offset = 0 }) => {
  const selectedClientServiceIdAtomValue = useAtomValue(
    selectedClientServiceIdAtom
  );

  const userAtomValue = useAtomValue(userAtom);
  const isClient = userAtomValue?.role === RoleEnum.CLIENT;

  return useQuery({
    queryKey: [
      "dashboard",
      "delivered-tasks",
      limit,
      offset,
      selectedClientServiceIdAtomValue,
    ],
    enabled: isClient && selectedClientServiceIdAtomValue > 0,
    queryFn: () =>
      apiClient.dashboardControllerGetDeliveredTasks({
        clientServiceId: selectedClientServiceIdAtomValue,
        limit,
        offset,
      }),
  });
};

export const useGetNewRequests = ({ limit = 10, offset = 0 }) => {
  const selectedClientServiceIdAtomValue = useAtomValue(
    selectedClientServiceIdAtom
  );

  const userAtomValue = useAtomValue(userAtom);
  const isCSM = userAtomValue?.role === RoleEnum.CSM;

  return useQuery({
    queryKey: [
      "dashboard",
      "orders",
      "without-csm",
      limit,
      offset,
      selectedClientServiceIdAtomValue,
    ],
    enabled: isCSM && selectedClientServiceIdAtomValue > 0,
    queryFn: () =>
      apiClient.dashboardControllerGetOrdersWithoutCSM({
        clientServiceId: selectedClientServiceIdAtomValue,
        limit,
        offset,
      }),
  });
};

export const useGetRetroplanningSent = () => {
  const selectedClientServiceIdAtomValue = useAtomValue(
    selectedClientServiceIdAtom
  );

  return useQuery({
    queryKey: [
      "dashboard",
      "retroplanning",
      "sent",
      selectedClientServiceIdAtomValue,
    ],
    enabled: selectedClientServiceIdAtomValue > 0,
    queryFn: () =>
      apiClient.dashboardControllerGetRetroplanningSent({
        clientServiceId: selectedClientServiceIdAtomValue,
      }),
  });
};

export const useGetDashboardMetrics = () => {
  const selectedClientServiceIdAtomValue = useAtomValue(
    selectedClientServiceIdAtom
  );

  return useQuery({
    queryKey: ["dashboard", "metrics", selectedClientServiceIdAtomValue],
    enabled: selectedClientServiceIdAtomValue > 0,
    queryFn: () =>
      apiClient.dashboardControllerGetDashboardMetrics({
        clientServiceId: selectedClientServiceIdAtomValue,
      }),
  });
};
